/**
 * Enhanced error handling and retry logic for authentication
 */

interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  jitter: boolean;
}

interface AuthError {
  type: 'NETWORK_ERROR' | 'SESSION_EXPIRED' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'RATE_LIMITED' | 'UNKNOWN';
  message: string;
  originalError?: any;
  retryable: boolean;
  retryAfter?: number; // seconds to wait before retrying
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 500,
  maxDelay: 5000,
  backoffFactor: 2,
  jitter: true,
};

/**
 * Classify authentication errors for appropriate handling
 */
export function classifyAuthError(error: any): AuthError {
  // Network-related errors
  if (error?.name === 'NetworkError' || error?.code === 'NETWORK_ERROR') {
    return {
      type: 'NETWORK_ERROR',
      message: 'Network connection failed. Please check your internet connection.',
      originalError: error,
      retryable: true,
    };
  }

  // HTTP status code based classification
  if (error?.status || error?.response?.status) {
    const status = error.status || error.response.status;
    
    switch (status) {
      case 401:
        return {
          type: 'UNAUTHORIZED',
          message: 'Your session has expired. Please log in again.',
          originalError: error,
          retryable: false,
        };
      
      case 403:
        return {
          type: 'FORBIDDEN',
          message: 'You do not have permission to access this resource.',
          originalError: error,
          retryable: false,
        };
      
      case 429:
        const retryAfter = parseInt(error.headers?.['retry-after']) || 60;
        return {
          type: 'RATE_LIMITED',
          message: 'Too many requests. Please wait before trying again.',
          originalError: error,
          retryable: true,
          retryAfter,
        };
      
      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: 'NETWORK_ERROR',
          message: 'Server temporarily unavailable. Please try again.',
          originalError: error,
          retryable: true,
        };
    }
  }

  // Session-specific errors
  if (error?.message?.includes('session') || error?.message?.includes('token')) {
    return {
      type: 'SESSION_EXPIRED',
      message: 'Your session has expired. Please log in again.',
      originalError: error,
      retryable: false,
    };
  }

  // Default classification
  return {
    type: 'UNKNOWN',
    message: error?.message || 'An unexpected error occurred.',
    originalError: error,
    retryable: false,
  };
}

/**
 * Calculate delay for exponential backoff with optional jitter
 */
function calculateDelay(attempt: number, config: RetryConfig): number {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffFactor, attempt - 1);
  const clampedDelay = Math.min(exponentialDelay, config.maxDelay);
  
  if (config.jitter) {
    // Add random jitter (±25% of the delay)
    const jitterRange = clampedDelay * 0.25;
    const jitter = (Math.random() - 0.5) * 2 * jitterRange;
    return Math.max(0, clampedDelay + jitter);
  }
  
  return clampedDelay;
}

/**
 * Enhanced retry mechanism with exponential backoff and error classification
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {},
  onRetry?: (attempt: number, error: AuthError) => void
): Promise<T> {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: AuthError;

  for (let attempt = 1; attempt <= finalConfig.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = classifyAuthError(error);
      
      // Don't retry if error is not retryable
      if (!lastError.retryable) {
        throw lastError;
      }
      
      // Don't retry on last attempt
      if (attempt >= finalConfig.maxAttempts) {
        throw lastError;
      }
      
      // Calculate delay (use retryAfter for rate limiting if available)
      const delay = lastError.retryAfter 
        ? lastError.retryAfter * 1000 
        : calculateDelay(attempt, finalConfig);
      
      // Notify about retry
      onRetry?.(attempt, lastError);
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

/**
 * Specialized auth operation wrapper with intelligent retry
 */
export async function executeAuthOperation<T>(
  operation: () => Promise<T>,
  operationName: string = 'auth operation',
  customConfig?: Partial<RetryConfig>
): Promise<T> {
  return retryWithBackoff(
    operation,
    customConfig,
    (attempt, error) => {
      console.warn(`[${operationName}] Attempt ${attempt} failed: ${error.message}. Retrying...`);
    }
  );
}

/**
 * Session verification with optimized retry strategy
 */
export async function verifySessionWithRetry(
  sessionFetcher: () => Promise<any>,
  config?: Partial<RetryConfig>
): Promise<any> {
  const sessionConfig = {
    maxAttempts: 5,
    baseDelay: 100,
    maxDelay: 2000,
    backoffFactor: 1.5, // More conservative backoff for session checks
    jitter: true,
    ...config,
  };

  return executeAuthOperation(
    async () => {
      const session = await sessionFetcher();
      if (!session?.data?.user) {
        throw new Error('No valid session found');
      }
      return session;
    },
    'session verification',
    sessionConfig
  );
}

/**
 * OAuth callback verification with enhanced error handling
 */
export async function verifyOAuthCallback(
  sessionFetcher: () => Promise<any>,
  redirectPath?: string
): Promise<{ session: any; shouldRedirect: boolean; redirectTo?: string }> {
  try {
    const session = await verifySessionWithRetry(sessionFetcher, {
      maxAttempts: 8, // More attempts for OAuth callbacks
      baseDelay: 200,
      maxDelay: 3000,
    });
    
    return {
      session,
      shouldRedirect: true,
      redirectTo: redirectPath || '/dashboard',
    };
  } catch (error) {
    const authError = classifyAuthError(error);
    
    console.error('[OAuth callback] Session verification failed:', authError);
    
    // Clear any stale session data
    try {
      sessionStorage.removeItem('TOKEN');
      sessionStorage.removeItem('CACHED_SESSION');
      sessionStorage.removeItem('CACHED_SESSION_TIMESTAMP');
    } catch {
      // Ignore storage errors
    }
    
    return {
      session: null,
      shouldRedirect: true,
      redirectTo: `/login?error=${encodeURIComponent(authError.message)}`,
    };
  }
}

/**
 * Handle auth errors with user-friendly messages and appropriate actions
 */
export function handleAuthError(error: AuthError): {
  userMessage: string;
  action: 'RETRY' | 'REDIRECT_LOGIN' | 'REDIRECT_HOME' | 'SHOW_ERROR';
  retryDelay?: number;
} {
  switch (error.type) {
    case 'NETWORK_ERROR':
      return {
        userMessage: 'Network connection issue. Please check your internet and try again.',
        action: 'RETRY',
        retryDelay: 3000,
      };
    
    case 'SESSION_EXPIRED':
    case 'UNAUTHORIZED':
      return {
        userMessage: 'Your session has expired. Please log in again.',
        action: 'REDIRECT_LOGIN',
      };
    
    case 'FORBIDDEN':
      return {
        userMessage: 'You do not have permission to access this page.',
        action: 'REDIRECT_HOME',
      };
    
    case 'RATE_LIMITED':
      return {
        userMessage: `Too many attempts. Please wait ${error.retryAfter || 60} seconds before trying again.`,
        action: 'RETRY',
        retryDelay: (error.retryAfter || 60) * 1000,
      };
    
    default:
      return {
        userMessage: 'An unexpected error occurred. Please try again.',
        action: 'SHOW_ERROR',
      };
  }
}